/**
 * The live call, docked over whatever page you are on.
 *
 * This is the answer to the thing that made the old console wrong: a call arrives while
 * you are in the middle of something, and being thrown onto a different page to write it
 * up is how remarks stop getting written. The dock floats above the current page instead
 * — Orders, Quotations, the calling queue, anywhere — so recording what was said never
 * costs you your place.
 *
 * On /sales-command-center the full panel is already on screen, so the dock stands down
 * rather than showing the same thing twice.
 */
import React from 'react';
import { useLocation } from 'react-router-dom';
import { FaPhoneAlt, FaChevronUp, FaTimes } from 'react-icons/fa';
import { useLiveCall } from './LiveCallProvider';
import LiveCallPanel from './LiveCallPanel';

const HOME = '/sales-command-center';
const mmss = (s) => `${Math.floor((s || 0) / 60)}:${String((s || 0) % 60).padStart(2, '0')}`;

export default function LiveCallDock() {
  const live = useLiveCall();
  const location = useLocation();

  if (!live?.isBusy) return null;
  // The page is already showing this call at full size.
  if (location.pathname === HOME) return null;

  if (live.minimised) {
    return (
      <button type="button" className="lc-pillbar" onClick={() => live.setMinimised(false)}>
        <span className="lc-dot" aria-hidden="true" />
        <span className="lc-pillbar-text">
          {live.isLive ? 'On a call' : 'Write-up open'} · {live.item?.company || 'record'}
        </span>
        <span className="lc-pillbar-timer">{mmss(live.seconds)}</span>
        <FaChevronUp size={11} />
      </button>
    );
  }

  return (
    <aside className="lc-dock" aria-label="Live call">
      <div className="lc-dock-bar">
        <FaPhoneAlt size={11} />
        <span>Live call</span>
        <button
          type="button"
          className="lc-dock-min"
          onClick={() => live.setMinimised(true)}
          title="Shrink — the call keeps running"
        >
          <FaTimes size={12} />
        </button>
      </div>
      <div className="lc-dock-body">
        <LiveCallPanel live={live} variant="dock" />
      </div>
    </aside>
  );
}
